import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { DailyCount } from '@/api/analytics';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';

interface MeetingFrequencyChartProps {
  data: DailyCount[];
}

const chartConfig = {
  count: {
    label: 'Meetings',
    color: 'hsl(var(--chart-3))',
  },
};

export function MeetingFrequencyChart({ data }: MeetingFrequencyChartProps) {
  // Transform data to include formatted labels
  const formattedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      dateLabel: format(parseISO(item.date), 'MMM d'),
      dateOriginal: item.date
    }));
  }, [data]);

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Meeting Frequency</CardTitle>
          <CardDescription>Number of meetings per day</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meeting Frequency</CardTitle>
        <CardDescription>Number of meetings per day</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={formattedData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="dateLabel" 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 12 }} width={40} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="var(--color-count)" 
                strokeWidth={2}
                name="Meetings"
                dot={{ fill: 'var(--color-count)', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
